/**
 * RS GraphQL Utils — minimal Storefront API query executor.
 * Reads config from window.RSStorefrontApiConfig (set by rs-product-core.liquid).
 */
(function () {
  'use strict';

  function _getConfig() {
    const config = window.RSStorefrontApiConfig;
    if (!config || !config.token) {
      throw new Error('[RSGraphQL] RSStorefrontApiConfig.token not available');
    }
    return config;
  }

  async function executeQuery(query, variables) {
    if (!query || typeof query !== 'string') {
      throw new Error('[RSGraphQL] query must be a non-empty string');
    }

    const config = _getConfig();
    const apiVersion = config.apiVersion || '2025-10';
    const url = `${window.location.origin}/api/${apiVersion}/graphql.json`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query, variables: variables || {} }),
    });

    if (!response.ok) {
      throw new Error('[RSGraphQL] HTTP ' + response.status);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error('[RSGraphQL] ' + JSON.stringify(result.errors));
    }

    return result.data;
  }

  /**
   * Fetches ALL metaobjects of a given type, extracts letter sub-objects from each one's `letters` references field, and filters by product title.
   * Used by templates where letters are nested inside the product's metaobject
   * (e.g. mint-ornament, path-bead) rather than fetched via a per-letter handle.
   */
  var _CUSTOMIZER_LETTERS_QUERY = `
    query GetCustomizerLetters($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        pageInfo { hasNextPage endCursor }
        edges {
          node {
            handle
            fields {
              key
              value
              references(first: 250) {
                edges {
                  node {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image { url width height }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  async function fetchCustomizerLetters(metaobjectType, options) {
    var opts = options || {};
    var filterBy = opts.filterBy || null;
    var allLetters = [];
    var hasNextPage = true;
    var cursor = null;

    while (hasNextPage) {
      var data = await executeQuery(_CUSTOMIZER_LETTERS_QUERY, {
        type: metaobjectType,
        first: 250,
        after: cursor,
      });

      if (!data || !data.metaobjects) break;

      var edges = data.metaobjects.edges;
      var pageInfo = data.metaobjects.pageInfo;

      edges.forEach(function (edge) {
        var node = edge.node;
        var fields = node.fields || [];
        var lettersField = fields.find(function (f) { return f.key === 'letters'; });
        if (!lettersField || !lettersField.references || !lettersField.references.edges) return;

        lettersField.references.edges.forEach(function (letterEdge) {
          var letter = letterEdge.node;
          if (!letter || !letter.fields) return;

          var entry = { key: null, name: null, image: null, left_recess: 0, right_recess: 0 };
          letter.fields.forEach(function (field) {
            if (field.key === 'key') {
              entry.key = field.value;
            } else if (field.key === 'character') {
              // Some metaobject types (e.g. bead-mine-kids) use 'character' field instead of 'key'.
              if (!entry.key) entry.key = field.value;
            } else if (field.key === 'name') {
              entry.name = field.value;
            } else if (field.key === 'image' && field.reference && field.reference.image) {
              entry.image = {
                url: field.reference.image.url,
                width: field.reference.image.width || null,
                height: field.reference.image.height || null,
              };
            } else if (field.key === 'left_recess') {
              entry.left_recess = parseFloat(field.value) || 0;
            } else if (field.key === 'right_recess') {
              entry.right_recess = parseFloat(field.value) || 0;
            }
          });

          if (!entry.key || !entry.image) return;
          if (filterBy && (!entry.name || entry.name.indexOf(filterBy) === -1)) return;
          allLetters.push(entry);
        });
      });

      hasNextPage = pageInfo.hasNextPage;
      cursor = pageInfo.endCursor;
    }

    return allLetters;
  }

  window.RSGraphQL = { executeQuery: executeQuery, fetchCustomizerLetters: fetchCustomizerLetters };
})();
